import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import objectPath from 'object-path';
import queryString from 'query-string';
import delay from 'lodash/delay';
import { History } from 'history';

// import JoyRideOnboardingGuide from 'src/onboarding/joyride-onboarding';
import { useJourneys } from 'src/store/journeys';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';
import {
  apiBulkDeleteJourneys,
  apiDeleteJourney,
  apiDuplicateJourney,
  apiFollowJourney,
  apiFollowSessionJourney,
  apiGetAllJourneys,
  apiGetJourneysByTags,
  apiUnfollowJourney,
  apiUnfollowSessionJourney,
  apiUpdateJourney,
} from 'src/utils/journeyApi';

import { DashboardHomeSection } from './section';
import { EventBus } from 'src/common/EventBus';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { Journey } from 'src/common/interfaces/journey.interface';
import { functionNoop } from 'src/utils/function/noop';
import { locationGetStringifiedSearchParams } from 'src/utils/location/get-stringified-search-params';
import { DashboardHomeJourneyListHeaderSortType } from './journeys/list';

import useDashboardActiveTab from '../helpers/active-tab.hook';
import { useCurrentSubscription } from 'src/store/subscription';

interface DashboardHomeProps {
  activeListType?: string;
  history: History;
}

const DEFAULT_JOURNEY_LIST_TYPE = 'all';
const ALL_JOURNEYS_PAGE_SIZE = 16;
const DEFAULT_JOURNEY_SORT_STACK: DashboardHomeJourneyListHeaderSortType = {
  orderBy: 'desc',
  sortName: 'updated_at',
};

export const DashboardHome = ({ history, activeListType }: DashboardHomeProps) => {
  useDashboardActiveTab('journeys');

  const { currentOrganization } = useCurrentOrganization();
  const { currentUser } = useCurrentUser();
  const { journeys, setJourneys, deleteJourney, prependJourney, updateJourney, setJourneysLoaded } = useJourneys();
  const { subscriptionPlanType } = useCurrentSubscription();

  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const [pageLoading, setPageLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);

  const {
    page = '1',
    search_str = '',
    order_by = null,
    sort_name = null,
    filters = null,
  } = queryString.parse(window.location.search) as Record<string, string>;

  const isSortEnabledByQueryParamsRef = useRef(order_by || sort_name ? false : true);
  const filtersRef = useRef(filters);

  const activeTableSortStackRef = useRef({
    orderBy: (order_by as typeof DEFAULT_JOURNEY_SORT_STACK['orderBy']) ?? DEFAULT_JOURNEY_SORT_STACK.orderBy,
    sortName: (sort_name as typeof DEFAULT_JOURNEY_SORT_STACK['sortName']) ?? DEFAULT_JOURNEY_SORT_STACK.sortName,
  });

  const isActiveListValid = useMemo(() => activeListType === DEFAULT_JOURNEY_LIST_TYPE, [activeListType]);

  const pageDetailsRef = useRef({ currentPage: parseInt(page), pages: 0 });
  const searchInputValueRef = useRef(search_str);

  const pushListTypeToHostName = useCallback(
    () =>
      history.replace({
        pathname: `${history.location.pathname}/${DEFAULT_JOURNEY_LIST_TYPE}`,
      }),
    [history]
  );

  useEffect(() => {
    const urlParts = history.location.pathname.split('/');
    if (!isActiveListValid) {
      if (urlParts.length === 4) {
        history.location.pathname = urlParts.slice(0, -1).join('/');
      }
      pushListTypeToHostName();
    }
  }, [isActiveListValid, history, pushListTypeToHostName]);

  const fetchJourneys = useCallback(async () => {
    const params: Record<string, any> = {
      searchStr: searchInputValueRef.current,
      page: pageDetailsRef.current.currentPage,
      per_page: ALL_JOURNEYS_PAGE_SIZE,
      list: DEFAULT_JOURNEY_LIST_TYPE,
      include_engagements: true,
      include_follows: true,
      include_steps: false,
    };

    if (activeTableSortStackRef.current) {
      params.orderBy = activeTableSortStackRef.current.orderBy;
      params.sortName = activeTableSortStackRef.current.sortName;
    }

    if (filtersRef.current && filtersRef.current.includes('tag_ids')) {
      const regex = /tag_ids:\((.*?)\)/;
      const match = filtersRef.current.match(regex);
      if (match) {
        params.tag_ids = match[1].replaceAll(`'`, '');
      }
    }

    const journeyResponse = await apiGetAllJourneys(currentOrganization.id, params);
    pageDetailsRef.current.pages = journeyResponse.pages;
    return journeyResponse.journeys;
  }, [currentOrganization.id]);

  const updateJourneyList = useCallback(
    ({ onBefore, onAfter }, skeletonTimeout = 0) => {
      return new Promise((resolve) => {
        onBefore() && onBefore();
        fetchJourneys().then((journeys) => {
          const disableLoading = () => {
            onAfter() && onAfter();
          };
          if (skeletonTimeout) {
            delay(disableLoading, skeletonTimeout);
          } else {
            disableLoading();
          }
          resolve(journeys);
        });
      });
    },
    [fetchJourneys]
  );

  const showListSpinner = useCallback(
    (isShown: boolean) => () => {
      setListLoading(isShown);
    },
    []
  );

  useEffect(() => {
    updateJourneyList({
      onBefore: () => {
        setPageLoading(true);
      },
      onAfter: () => {
        setPageLoading(false);
      },
    }).then((journeys) => {
      setJourneys(journeys, subscriptionPlanType);
      setJourneysLoaded(true);
    });
  }, [subscriptionPlanType, setJourneys, setJourneysLoaded, updateJourneyList]);

  useEffect(() => {
    const onJourneyLinkCopied = () => {
      setSuccessNotification('URL copied to clipboard');
    };

    const onJourneyRemove = async (uuid: string) => {
      // cover any error
      await apiDeleteJourney(uuid);
      deleteJourney(uuid, subscriptionPlanType);
      setSuccessNotification('Journey deleted');
    };
    const onJourneyBulkDelete = async (journeyIds: string[]) => {
      try {
        const result = await apiBulkDeleteJourneys(journeyIds);
        setSuccessNotification(`Successfully deleted ${result.deleted_count} item(s)`);
        updateJourneyListWithSpinner(true);
      } catch (error) {
        console.error('Error deleting contents:', error);
        setErrorNotification('Failed to delete selected items');
      }
    };
    const onJourneyDuplicated = async (data: { uuid: string; detach_synced_step: boolean }) => {
      const params: Record<string, boolean> = {};
      if (data.detach_synced_step !== undefined) {
        params.detach_synced_step = data.detach_synced_step;
      }
      // cover any error
      const response: Journey = await apiDuplicateJourney(data.uuid, params);
      prependJourney(response, subscriptionPlanType);
      setSuccessNotification(`${response.name} created`);
    };

    const onJourneyMoved = async (data: { uuid: string; workspaceId: string }) => {
      await apiUpdateJourney(data.uuid, { workspace_id: data.workspaceId }).then(updateJourney);
      setSuccessNotification('Journey was moved');
    };

    const onJourneyStatusChanged = async (data: { uuid: string; status: string }) => {
      let statusMessage = 'saved as a draft';
      if ('public' === data.status) {
        statusMessage = 'published';
      }
      await apiUpdateJourney(data.uuid, { status: data.status }).then(updateJourney);
      setSuccessNotification(`The journey has been ${statusMessage} successfully.`);
    };

    const onJourneySocialSharingChanged = async (data: { uuid: string; social_sharing: boolean }) => {
      let statusMessage = 'disabled';
      if (true === data.social_sharing) {
        statusMessage = 'actived';
      }
      await apiUpdateJourney(data.uuid, { social_sharing: data.social_sharing }).then(updateJourney);
      setSuccessNotification(`The journey social sharing has been ${statusMessage} successfully.`);
    };

    const onJourneyNotificationChanged = async (data: { uuid: string; isFollowed: boolean }) => {
      let requestAdapter = apiUnfollowJourney;
      let subscriptionMessage = 'Unsubscribed';
      if (data.isFollowed) {
        subscriptionMessage = 'Subscribed';
        requestAdapter = apiFollowJourney;
      }
      await requestAdapter(data.uuid);
      setSuccessNotification(`${subscriptionMessage} to notifications successfully`);
    };

    const onJourneySessionNotificationChanged = async (data: { uuid: string; isFollowed: boolean }) => {
      let requestAdapter = apiUnfollowSessionJourney;
      let subscriptionMessage = 'Unsubscribed';
      if (data.isFollowed) {
        subscriptionMessage = 'Subscribed';
        requestAdapter = apiFollowSessionJourney;
      }
      await requestAdapter(data.uuid);
      setSuccessNotification(`${subscriptionMessage} to session notifications successfully`);
    };

    const replaceUrlSearchParams = () => {
      const currentSearchParams = new URLSearchParams(history.location.search);
      const searchParams: Record<string, string> = {};

      if (currentSearchParams.has('filters') && currentSearchParams.get('filters') !== '') {
        searchParams.filters = currentSearchParams.get('filters')!;
      }

      if (pageDetailsRef.current.currentPage !== 1) {
        searchParams.page = `${pageDetailsRef.current.currentPage}`;
      }

      if (activeTableSortStackRef.current && isSortEnabledByQueryParamsRef.current) {
        searchParams.order_by = activeTableSortStackRef.current.orderBy;
        searchParams.sort_name = activeTableSortStackRef.current.sortName;
      }

      history.replace({
        pathname: history.location.pathname,
        search: locationGetStringifiedSearchParams(history.location.search, searchParams),
      });
    };

    const updateJourneyListWithSpinner = (isEnabled: boolean = false, updateFromServer: boolean = true) => {
      if (updateFromServer) {
        updateJourneyList({
          onBefore: isEnabled ? showListSpinner(true) : functionNoop,
          onAfter: isEnabled ? showListSpinner(false) : functionNoop,
        }).then((journeys) => {
          setJourneys(journeys, subscriptionPlanType);
          replaceUrlSearchParams();
        });
      } else {
        const { sortName, orderBy } = activeTableSortStackRef.current;
        const field = ['creator', 'workspace'].includes(sortName) ? `${sortName}.name` : sortName;
        setJourneys(
          journeys.sort((a: Journey, b: Journey) => {
            const aValue = objectPath.get(a, field);
            const bValue = objectPath.get(b, field);

            if (aValue < bValue) {
              return orderBy === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return orderBy === 'asc' ? 1 : -1;
            }
            // a must be equal to b
            return 0;
          }),
          subscriptionPlanType
        );
        replaceUrlSearchParams();
      }
    };

    const onSearchInputChanged = (value: string) => {
      pageDetailsRef.current.currentPage = 1;
      searchInputValueRef.current = value;
      updateJourneyListWithSpinner(true);
    };

    const onCurrentPageChanged = (currentPage: number) => {
      pageDetailsRef.current.currentPage = currentPage;
      updateJourneyListWithSpinner(true);
    };

    const onTableSortChanged = (sortStack: typeof DEFAULT_JOURNEY_SORT_STACK) => {
      isSortEnabledByQueryParamsRef.current = true;
      activeTableSortStackRef.current = sortStack;
      updateJourneyListWithSpinner(true, true);
    };

    const onWorkspaceChanged = () => updateJourneyListWithSpinner(true);

    const onJourneyChanged = (updatedJourney: Journey) => {
      updateJourney(updatedJourney);
    };

    const onJourneyTagsChange = (tags: string, isEnabled: boolean = false) => {
      if (tags && tags.length > 0) {
        filtersRef.current = `(tag_ids:(${tags
          .split(',')
          .map((tag) => `'${tag.trim()}'`)
          .join(',')}))`;
      } else {
        filtersRef.current = null;
      }
      updateJourneyList({
        onBefore: isEnabled ? showListSpinner(true) : functionNoop,
        onAfter: isEnabled ? showListSpinner(false) : functionNoop,
      }).then((journeys) => {
        setJourneys(journeys, subscriptionPlanType);
      });
    };

    EventBus.on('home:journey-link-copy', onJourneyLinkCopied);
    EventBus.on('home:journey-delete', onJourneyRemove);
    EventBus.on('home:journey-bulk-delete', onJourneyBulkDelete);
    EventBus.on('home:journey-duplicate', onJourneyDuplicated);
    EventBus.on('home:journey-move', onJourneyMoved);
    EventBus.on('home:journey-tags-change', onJourneyTagsChange);
    EventBus.on('home:journey-change', onJourneyChanged);
    EventBus.on('home:journey-notification', onJourneyNotificationChanged);
    EventBus.on('home:journey-session-notification',onJourneySessionNotificationChanged);
    EventBus.on('home:journey-status', onJourneyStatusChanged);
    EventBus.on('home:journey-is-sharing', onJourneySocialSharingChanged);
    EventBus.on('home:journey-search', onSearchInputChanged);
    EventBus.on('home:journey-page-change', onCurrentPageChanged);
    EventBus.on('home:journey-sort-change', onTableSortChanged);
    EventBus.on('home:workspace-change', onWorkspaceChanged);

    return () => {
      EventBus.off('home:journey-link-copy', onJourneyLinkCopied);
      EventBus.off('home:journey-delete', onJourneyRemove);
      EventBus.off('home:journey-bulk-delete', onJourneyBulkDelete);
      EventBus.off('home:journey-duplicate', onJourneyDuplicated);
      EventBus.off('home:journey-move', onJourneyMoved);
      EventBus.off('home:journey-tags-change', onJourneyTagsChange);
      EventBus.off('home:journey-change', onJourneyChanged);
      EventBus.off('home:journey-notification', onJourneyNotificationChanged);
      EventBus.off('home:journey-session-notification',onJourneySessionNotificationChanged);
      EventBus.off('home:journey-status', onJourneyStatusChanged);
      EventBus.off('home:journey-is-sharing', onJourneySocialSharingChanged);
      EventBus.off('home:journey-search', onSearchInputChanged);
      EventBus.off('home:journey-page-change', onCurrentPageChanged);
      EventBus.off('home:journey-sort-change', onTableSortChanged);
      EventBus.off('home:workspace-change', onWorkspaceChanged);
    };
  }, [
    deleteJourney,
    updateJourney,
    prependJourney,
    setSuccessNotification,
    updateJourneyList,
    history.location.pathname,
    history.location,
    pushListTypeToHostName,
    subscriptionPlanType,
    setJourneys,
    history,
    showListSpinner,
    journeys,
  ]);

  if (!currentUser) {
    return null;
  }

  const hideDashboardContent =
    journeys.length === 0 &&
    searchInputValueRef.current === '' &&
    pageDetailsRef.current.currentPage === 1 &&
    filters === '';

  return (
    <>
      {/* <JoyRideOnboardingGuide initial={true} /> */}
      <Helmet>
        <title>Journey | Dashboard</title>
      </Helmet>
      <div className='flex flex-col w-full'>
        <DashboardHomeSection
          loading={pageLoading}
          hideDashboardContent={hideDashboardContent}
          isListLoading={listLoading}
          journeys={journeys}
          searchInputValue={searchInputValueRef.current}
          pagination={pageDetailsRef.current}
          currentUser={currentUser}
          activeTableSortType={activeTableSortStackRef.current}
        />
      </div>
    </>
  );
};
